/**
 * Wie ziet wat — de frontend-kant van de rechten in de API.
 *
 * Sinds 2026-10-12 staat per rol in de database wat hij mag (Beheer › Rechten,
 * docs/auth.md). De sessie (`GET /auth/me`) geeft de rechten van deze gebruiker
 * mee; admin heeft ze allemaal. Dit bestand bepaalt **niets**: elk endpoint heeft
 * in de API zijn eigen `requirePermission`. Wat het wél doet is voorkomen dat
 * iemand een menu-item, tegel of knop ziet die hem alleen een 403 oplevert. Als de
 * twee uit elkaar lopen wint de API.
 *
 * De sleutels hieronder zijn een kopie van `services/api/src/lib/permissionCatalog.ts`;
 * `access.test.ts` vergelijkt ze, zodat een nieuwe sleutel niet aan één kant blijft
 * hangen.
 */

export const ALL_ROLES = [
  'admin',
  'planner',
  'werkvoorbereider',
  'administratie',
  'logistiek',
] as const;

export type Role = (typeof ALL_ROLES)[number];

export const PERMISSIONS = [
  'coordination',
  'coordination_write',
  'groundwork',
  'groundwork_resend',
  'additional_work_notice',
  'additional_work_notice_send',
  'fuel',
  'fuel_write',
  'staging',
  'staging_stage',
  'staging_unstage_any',
  'staging_sms_numbers',
  'loading_places_write',
  'storage',
  'storage_write',
  'storage_admin',
  'storage_dashboard',
  'quotations',
  'quotations_settings',
  'users',
  'users_notifications',
  'data_owners',
  'data_owners_write',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** Wat een gate vraagt: één van deze rechten, of beheer zelf. */
export type Access = readonly Permission[] | 'admin';

/** The part of the session this file reads. */
export interface AccessSession {
  roles: readonly string[];
  permissions: readonly string[];
  rolesWithWork?: readonly string[];
}

/** Beheer: altijd alles, en als enige het uitdelen van toegang. */
export function isAdmin(session: AccessSession | null | undefined): boolean {
  return !!session?.roles.includes('admin');
}

/** Heeft deze sessie één van deze rechten? */
export function can(session: AccessSession | null | undefined, ...keys: Permission[]): boolean {
  if (!session) return false;
  return keys.some((k) => session.permissions.includes(k));
}

export function mayAccess(session: AccessSession | null | undefined, access: Access): boolean {
  return access === 'admin' ? isAdmin(session) : can(session, ...access);
}

/**
 * Heeft een rol eigen werk — iets te dóen, niet alleen te kijken? Bepaalt alleen
 * de vormgeving (een gestippelde rol-chip); de API zegt welke rollen dat zijn.
 */
export function roleHasWork(session: AccessSession | null | undefined, role: string): boolean {
  return role === 'admin' || !!session?.rolesWithWork?.includes(role);
}

/** Heeft deze gebruiker zelf eigen werk? Zo niet, dan zegt het dashboard dat eerlijk. */
export function sessionHasWork(session: AccessSession | null | undefined): boolean {
  return !!session?.roles.some((r) => roleHasWork(session, r));
}

/**
 * Pad-prefix → wat het vraagt. Langste prefix wint, zodat
 * `/logistics/loading-places` (beheren) niet onder `/logistics` (bekijken) valt.
 *
 * Een pad dat hier niet staat is voor iedereen met een sessie — dashboard en
 * voorkeuren horen bij geen module.
 */
const PATH_ACCESS: [string, Access][] = [
  ['/logistics/loading-places', ['loading_places_write']],
  // Instellen heeft een eigen prefix, zodat een gewone prefix volstaat (handoff Opslag).
  ['/storage/floorplan-settings', ['storage_admin']],
  ['/storage/station-types', ['storage_admin']],
  ['/storage/owners', ['storage_admin']],
  ['/storage/dashboard', ['storage_dashboard']],
  ['/storage', ['storage']],
  ['/orders', ['coordination']],
  ['/intake', ['coordination']],
  ['/fuel', ['fuel']],
  ['/logistics', ['staging']],
  ['/users', ['users']],
  ['/data-owners', ['data_owners']],
  ['/groundwork', ['groundwork']],
  ['/additional-work', ['additional_work_notice']],
  ['/quotations', ['quotations']],
  ['/relations', ['quotations']],
  ['/quotation-lists', ['quotations_settings']],
  ['/permissions', 'admin'],
];

/**
 * Mag deze sessie dit pad openen?
 *
 * Het menu verbergt wat niet mag, maar een bladwijzer of een oude link niet. Zonder
 * deze controle kreeg een rol zonder rechten de **lege staat** van het scherm te
 * zien — "Nog geen opdrachten" terwijl de API 403 antwoordde. Niet mogen kijken is
 * iets anders dan niets te zien hebben, en het scherm hoort dat verschil te maken.
 */
export function mayOpenPath(pathname: string, session: AccessSession | null | undefined): boolean {
  const hit = PATH_ACCESS.filter(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  ).sort((a, b) => b[0].length - a[0].length)[0];
  if (!hit) return true;
  return mayAccess(session, hit[1]);
}
